// BaaliLogo — the platform brand mark + wordmark.
//
// PRIMARY: loads the icon you upload at /admin/app-icons (per role —
// /icons-admin/icon-128.png for the admin app, /icons-agent/... for
// the agent app, /icons/... for the player app). That's the
// authoritative platform brand image.
//
// FALLBACK: if the upload doesn't exist, render an inline SVG B
// monogram so the page never shows a broken image. The fallback is
// only seen on a fresh install before any icon has been uploaded.

import { useState } from 'react';

type Props = {
  role?: 'admin' | 'agent';
  wordmark?: boolean;
  /** Pixel size of the mark (height of the rounded square). */
  size?: number;
  /** For agents: the role/level name to show as the badge (Super Master / Master /
   *  Agent). Defaults to "Agent" if not supplied. */
  levelLabel?: string;
  /** For agents: the logged-in username, shown next to the badge so it's always
   *  clear WHO is signed in. */
  username?: string;
};

const ICON_SRC: Record<'admin' | 'agent', string> = {
  admin: '/icons-admin/icon-128.png',
  agent: '/icons-agent/icon-128.png',
};

export default function BaaliLogo({ role = 'admin', wordmark = true, size = 36, levelLabel, username }: Props) {
  // We try the uploaded file first; if it 404s, the <img> onError
  // fires once and we permanently flip to the SVG fallback for this
  // mount. Bust the cache on uploads is the admin's responsibility
  // (the AppIcons page already appends ?t=... after upload).
  const [usingFallback, setUsingFallback] = useState(false);

  return (
    <div className="flex items-center gap-2 min-w-0 shrink-0">
      <span
        className={`relative grid place-items-center rounded-xl shrink-0 overflow-hidden
                    ${usingFallback
                      ? 'bg-gradient-to-br from-violet-500 to-fuchsia-600 shadow-[0_0_24px_-4px_rgba(139,92,246,0.55)]'
                      : 'bg-white dark:bg-white/5 ring-1 ring-neutral-200 dark:ring-white/10'}`}
        style={{ width: size, height: size }}
      >
        {usingFallback ? (
          <>
            <svg viewBox="0 0 24 24" width={Math.round(size * 0.6)} height={Math.round(size * 0.6)} fill="none" aria-hidden="true">
              <path
                d="M 6 4 L 6 20 L 13 20 C 16.2 20 18.4 18.3 18.4 15.6 C 18.4 13.9 17.5 12.7 16 12.1 C 17.2 11.5 17.8 10.4 17.8 8.8 C 17.8 6 15.8 4 12.6 4 Z M 9 6.6 L 12.2 6.6 C 13.7 6.6 14.6 7.4 14.6 8.8 C 14.6 10.2 13.7 11 12.2 11 L 9 11 Z M 9 13.4 L 12.6 13.4 C 14.4 13.4 15.4 14.3 15.4 15.7 C 15.4 17.2 14.4 18 12.6 18 L 9 18 Z"
                fill="white"
              />
            </svg>
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-fuchsia-300 ring-2 ring-violet-700" />
          </>
        ) : (
          <img
            src={ICON_SRC[role]}
            alt={role === 'agent' ? 'Baali Agent' : 'Baali'}
            width={size} height={size}
            className="w-full h-full object-cover"
            onError={() => setUsingFallback(true)}
          />
        )}
      </span>
      {wordmark && (
        <span className="hidden sm:inline-flex items-center gap-1.5">
          <span className="text-base sm:text-lg font-display font-extrabold text-neutral-900 dark:text-white tracking-tight leading-none">
            Baali
          </span>
          {role === 'agent' && (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span className="text-[9.5px] font-bold uppercase tracking-wider
                               text-emerald-700 dark:text-emerald-300
                               bg-emerald-50 dark:bg-emerald-400/15
                               border border-emerald-200 dark:border-emerald-400/30
                               px-1.5 py-0.5 rounded whitespace-nowrap">
                {levelLabel || 'Agent'}
              </span>
              {username && (
                <span className="text-[11px] font-semibold text-neutral-500 dark:text-neon-200/60 truncate max-w-[9rem]">
                  @{username}
                </span>
              )}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
